/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  FlightRule,
  IntegrationSystem,
  UnitStatus,
  WeatherStatus,
} from 'src/graphql/generated/graphql.generated';
import {
  CrewSourcedUnitRecord,
  CrewSourcedUnitProps,
} from 'src/store/generated/crew-sourced-unit-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitProps {
  id: string;
  name: string;
  abbreviation: string;
  status: UnitStatus;
  weatherStatus: WeatherStatus;
  baseId: string;
  isAdministrative: boolean | null;
  vehicleId: string | null;
  regionId: string;
  isHidden: boolean | null;
  isThirdPartyUnit: boolean | null;
  etloInMinutes: number | null;
  pilotNotes: string | null;
  lastPilotUpdateTime: Date | null;
  lastPilotUpdateById: string | null;
  isExternallyManaged: boolean;
  maximumFlightRule: FlightRule;
  isOnDelay: boolean;
  plannedAvailableNotes: string | null;
  plannedAvailableTime: Date | null;
  isEnabled: boolean;
  isSpare: boolean;
  oemPlacardNumber: string | null;
  primaryOutOfServiceEventId: string | null;
  emSystemResourceId: string | null;
  integrations: Set<IntegrationSystem>;
  onBaseETAInMinutes: number | null;
  crewSourcedFromUnitId: string | null;
  completeFlightInstanceId: string | null;
  isCrewAvailable: boolean;
  updatePhoneNumbersFromExternal: boolean;
  crewSourcedToUnits: List<CrewSourcedUnitProps> | null;
  excludeStates: List<string> | null;
  parentUnitId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitRecord extends Record<UnitProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  status: null as any,
  weatherStatus: null as any,
  baseId: null as any,
  isAdministrative: null as any,
  vehicleId: null as any,
  regionId: null as any,
  isHidden: null as any,
  isThirdPartyUnit: null as any,
  etloInMinutes: null as any,
  pilotNotes: null as any,
  lastPilotUpdateTime: null as any,
  lastPilotUpdateById: null as any,
  isExternallyManaged: null as any,
  maximumFlightRule: null as any,
  isOnDelay: null as any,
  plannedAvailableNotes: null as any,
  plannedAvailableTime: null as any,
  isEnabled: null as any,
  isSpare: null as any,
  oemPlacardNumber: null as any,
  primaryOutOfServiceEventId: null as any,
  emSystemResourceId: null as any,
  integrations: null as any,
  onBaseETAInMinutes: null as any,
  crewSourcedFromUnitId: null as any,
  completeFlightInstanceId: null as any,
  isCrewAvailable: null as any,
  updatePhoneNumbersFromExternal: null as any,
  crewSourcedToUnits: null as any,
  excludeStates: null as any,
  parentUnitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    status: UnitStatus;
    weatherStatus: WeatherStatus;
    baseId: string;
    isAdministrative: boolean | null;
    vehicleId: string | null;
    regionId: string;
    isHidden: boolean | null;
    isThirdPartyUnit: boolean | null;
    etloInMinutes: number | null;
    pilotNotes: string | null;
    lastPilotUpdateTime: string | number | Date | null;
    lastPilotUpdateById: string | null;
    isExternallyManaged: boolean;
    maximumFlightRule: FlightRule;
    isOnDelay: boolean;
    plannedAvailableNotes: string | null;
    plannedAvailableTime: string | number | Date | null;
    isEnabled: boolean;
    isSpare: boolean;
    oemPlacardNumber: string | null;
    primaryOutOfServiceEventId: string | null;
    emSystemResourceId: string | null;
    integrations: any;
    onBaseETAInMinutes: number | null;
    crewSourcedFromUnitId: string | null;
    completeFlightInstanceId: string | null;
    isCrewAvailable: boolean;
    updatePhoneNumbersFromExternal: boolean;
    crewSourcedToUnits: any;
    excludeStates: any;
    parentUnitId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitRecord {
    if (json == null) {
      throw new Error('UnitRecord.fromJS: parameter cannot be null');
    }
    return new UnitRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'UnitJson.abbreviation'),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
        'UnitJson.status',
      ),
      weatherStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'weatherStatus', WeatherStatus.Unspecified),
        'UnitJson.weatherStatus',
      ),
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'UnitJson.baseId',
      ),
      isAdministrative: json.isAdministrative,
      vehicleId: json.vehicleId,
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'UnitJson.regionId',
      ),
      isHidden: json.isHidden,
      isThirdPartyUnit: json.isThirdPartyUnit,
      etloInMinutes: json.etloInMinutes,
      pilotNotes: json.pilotNotes,
      lastPilotUpdateTime:
        json.lastPilotUpdateTime != null ? new Date(json.lastPilotUpdateTime) : null,
      lastPilotUpdateById: json.lastPilotUpdateById,
      isExternallyManaged: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isExternallyManaged', false),
        'UnitJson.isExternallyManaged',
      ),
      maximumFlightRule: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'maximumFlightRule', FlightRule.Ifr),
        'UnitJson.maximumFlightRule',
      ),
      isOnDelay: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isOnDelay', false),
        'UnitJson.isOnDelay',
      ),
      plannedAvailableNotes: json.plannedAvailableNotes,
      plannedAvailableTime:
        json.plannedAvailableTime != null ? new Date(json.plannedAvailableTime) : null,
      isEnabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isEnabled', false),
        'UnitJson.isEnabled',
      ),
      isSpare: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSpare', false),
        'UnitJson.isSpare',
      ),
      oemPlacardNumber: json.oemPlacardNumber,
      primaryOutOfServiceEventId: json.primaryOutOfServiceEventId,
      emSystemResourceId: json.emSystemResourceId,
      integrations: Set<IntegrationSystem>(
        getFromOrThrow(json, (j) => j.integrations, 'UnitJson.integrations'),
      ),
      onBaseETAInMinutes: json.onBaseETAInMinutes,
      crewSourcedFromUnitId: json.crewSourcedFromUnitId,
      completeFlightInstanceId: json.completeFlightInstanceId,
      isCrewAvailable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isCrewAvailable', false),
        'UnitJson.isCrewAvailable',
      ),
      updatePhoneNumbersFromExternal: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'updatePhoneNumbersFromExternal', false),
        'UnitJson.updatePhoneNumbersFromExternal',
      ),
      crewSourcedToUnits:
        json.crewSourcedToUnits != null
          ? List<CrewSourcedUnitRecord>(
              json.crewSourcedToUnits.map((o: any) => new CrewSourcedUnitRecord(o)),
            )
          : null,
      excludeStates:
        json.excludeStates != null
          ? List<string>(json.excludeStates.map((o: any) => new String(o)))
          : null,
      parentUnitId: json.parentUnitId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    status: UnitStatus;
    weatherStatus: WeatherStatus;
    baseId: string;
    isAdministrative: boolean | null;
    vehicleId: string | null;
    regionId: string;
    isHidden: boolean | null;
    isThirdPartyUnit: boolean | null;
    etloInMinutes: number | null;
    pilotNotes: string | null;
    lastPilotUpdateTime: string | number | Date | null;
    lastPilotUpdateById: string | null;
    isExternallyManaged: boolean;
    maximumFlightRule: FlightRule;
    isOnDelay: boolean;
    plannedAvailableNotes: string | null;
    plannedAvailableTime: string | number | Date | null;
    isEnabled: boolean;
    isSpare: boolean;
    oemPlacardNumber: string | null;
    primaryOutOfServiceEventId: string | null;
    emSystemResourceId: string | null;
    integrations: any;
    onBaseETAInMinutes: number | null;
    crewSourcedFromUnitId: string | null;
    completeFlightInstanceId: string | null;
    isCrewAvailable: boolean;
    updatePhoneNumbersFromExternal: boolean;
    crewSourcedToUnits: any;
    excludeStates: any;
    parentUnitId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'UnitJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', UnitStatus.Available),
            'UnitJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weatherStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'weatherStatus', WeatherStatus.Unspecified),
            'UnitJson.weatherStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'UnitJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAdministrative: tryOrDefault(
        () => json.isAdministrative,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'UnitJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isHidden: tryOrDefault(
        () => json.isHidden,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isThirdPartyUnit: tryOrDefault(
        () => json.isThirdPartyUnit,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etloInMinutes: tryOrDefault(
        () => json.etloInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pilotNotes: tryOrDefault(
        () => json.pilotNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPilotUpdateTime: tryOrDefault(
        () => (json.lastPilotUpdateTime != null ? new Date(json.lastPilotUpdateTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPilotUpdateById: tryOrDefault(
        () => json.lastPilotUpdateById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isExternallyManaged: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isExternallyManaged', false),
            'UnitJson.isExternallyManaged',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maximumFlightRule: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'maximumFlightRule', FlightRule.Ifr),
            'UnitJson.maximumFlightRule',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOnDelay: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isOnDelay', false),
            'UnitJson.isOnDelay',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedAvailableNotes: tryOrDefault(
        () => json.plannedAvailableNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedAvailableTime: tryOrDefault(
        () => (json.plannedAvailableTime != null ? new Date(json.plannedAvailableTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEnabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isEnabled', false),
            'UnitJson.isEnabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSpare: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'isSpare', false), 'UnitJson.isSpare'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemPlacardNumber: tryOrDefault(
        () => json.oemPlacardNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryOutOfServiceEventId: tryOrDefault(
        () => json.primaryOutOfServiceEventId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emSystemResourceId: tryOrDefault(
        () => json.emSystemResourceId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      integrations: tryOrDefault(
        () =>
          Set<IntegrationSystem>(
            getFromOrThrow(json, (j) => j.integrations, 'UnitJson.integrations'),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onBaseETAInMinutes: tryOrDefault(
        () => json.onBaseETAInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewSourcedFromUnitId: tryOrDefault(
        () => json.crewSourcedFromUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      completeFlightInstanceId: tryOrDefault(
        () => json.completeFlightInstanceId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isCrewAvailable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isCrewAvailable', false),
            'UnitJson.isCrewAvailable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      updatePhoneNumbersFromExternal: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'updatePhoneNumbersFromExternal', false),
            'UnitJson.updatePhoneNumbersFromExternal',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewSourcedToUnits: tryOrDefault(
        () =>
          json.crewSourcedToUnits != null
            ? List<CrewSourcedUnitRecord>(
                json.crewSourcedToUnits.map((o: any) => new CrewSourcedUnitRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      excludeStates: tryOrDefault(
        () =>
          json.excludeStates != null
            ? List<string>(json.excludeStates.map((o: any) => new String(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentUnitId: tryOrDefault(
        () => json.parentUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitViewModelBase implements UnitProps {
  protected readonly _props: UnitProps;
  private _json?: UnitProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get status(): UnitStatus {
    return this._props.status;
  }
  get weatherStatus(): WeatherStatus {
    return this._props.weatherStatus;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get isAdministrative(): boolean | null {
    return this._props.isAdministrative;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get isHidden(): boolean | null {
    return this._props.isHidden;
  }
  get isThirdPartyUnit(): boolean | null {
    return this._props.isThirdPartyUnit;
  }
  get etloInMinutes(): number | null {
    return this._props.etloInMinutes;
  }
  get pilotNotes(): string | null {
    return this._props.pilotNotes;
  }
  get lastPilotUpdateTime(): Date | null {
    return this._props.lastPilotUpdateTime;
  }
  get lastPilotUpdateById(): string | null {
    return this._props.lastPilotUpdateById;
  }
  get isExternallyManaged(): boolean {
    return this._props.isExternallyManaged;
  }
  get maximumFlightRule(): FlightRule {
    return this._props.maximumFlightRule;
  }
  get isOnDelay(): boolean {
    return this._props.isOnDelay;
  }
  get plannedAvailableNotes(): string | null {
    return this._props.plannedAvailableNotes;
  }
  get plannedAvailableTime(): Date | null {
    return this._props.plannedAvailableTime;
  }
  get isEnabled(): boolean {
    return this._props.isEnabled;
  }
  get isSpare(): boolean {
    return this._props.isSpare;
  }
  get oemPlacardNumber(): string | null {
    return this._props.oemPlacardNumber;
  }
  get primaryOutOfServiceEventId(): string | null {
    return this._props.primaryOutOfServiceEventId;
  }
  get emSystemResourceId(): string | null {
    return this._props.emSystemResourceId;
  }
  get integrations(): Set<IntegrationSystem> {
    return this._props.integrations;
  }
  get onBaseETAInMinutes(): number | null {
    return this._props.onBaseETAInMinutes;
  }
  get crewSourcedFromUnitId(): string | null {
    return this._props.crewSourcedFromUnitId;
  }
  get completeFlightInstanceId(): string | null {
    return this._props.completeFlightInstanceId;
  }
  get isCrewAvailable(): boolean {
    return this._props.isCrewAvailable;
  }
  get updatePhoneNumbersFromExternal(): boolean {
    return this._props.updatePhoneNumbersFromExternal;
  }
  get crewSourcedToUnits(): List<CrewSourcedUnitProps> | null {
    return this._props.crewSourcedToUnits;
  }
  get excludeStates(): List<string> | null {
    return this._props.excludeStates;
  }
  get parentUnitId(): string | null {
    return this._props.parentUnitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAvailable() {
    return this._props.status === UnitStatus.Available;
  }
  public get isCommitted() {
    return this._props.status === UnitStatus.Committed;
  }
  public get isOnStandby() {
    return this._props.status === UnitStatus.OnStandby;
  }
  public get isOutOfService() {
    return this._props.status === UnitStatus.OutOfService;
  }
  public get isPartiallyAvailable() {
    return this._props.status === UnitStatus.PartiallyAvailable;
  }
  public get isGreen() {
    return this._props.weatherStatus === WeatherStatus.Green;
  }
  public get isRed() {
    return this._props.weatherStatus === WeatherStatus.Red;
  }
  public get isUnspecified() {
    return this._props.weatherStatus === WeatherStatus.Unspecified;
  }
  public get isYellow() {
    return this._props.weatherStatus === WeatherStatus.Yellow;
  }
  public get isIfr() {
    return this._props.maximumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.maximumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.maximumFlightRule === FlightRule.Vfr;
  }

  public isAnyStatus(...unitStatus: UnitStatus[]) {
    return unitStatus.includes(this._props.status);
  }
  public isAnyWeatherStatus(...weatherStatus: WeatherStatus[]) {
    return weatherStatus.includes(this._props.weatherStatus);
  }
  public isAnyMaximumFlightRule(...flightRule: FlightRule[]) {
    return flightRule.includes(this._props.maximumFlightRule);
  }

  constructor(props: UnitProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        status: this._props.status,
        weatherStatus: this._props.weatherStatus,
        baseId: this._props.baseId,
        isAdministrative: this._props.isAdministrative,
        vehicleId: this._props.vehicleId,
        regionId: this._props.regionId,
        isHidden: this._props.isHidden,
        isThirdPartyUnit: this._props.isThirdPartyUnit,
        etloInMinutes: this._props.etloInMinutes,
        pilotNotes: this._props.pilotNotes,
        lastPilotUpdateTime: this._props.lastPilotUpdateTime,
        lastPilotUpdateById: this._props.lastPilotUpdateById,
        isExternallyManaged: this._props.isExternallyManaged,
        maximumFlightRule: this._props.maximumFlightRule,
        isOnDelay: this._props.isOnDelay,
        plannedAvailableNotes: this._props.plannedAvailableNotes,
        plannedAvailableTime: this._props.plannedAvailableTime,
        isEnabled: this._props.isEnabled,
        isSpare: this._props.isSpare,
        oemPlacardNumber: this._props.oemPlacardNumber,
        primaryOutOfServiceEventId: this._props.primaryOutOfServiceEventId,
        emSystemResourceId: this._props.emSystemResourceId,
        integrations: this._props.integrations,
        onBaseETAInMinutes: this._props.onBaseETAInMinutes,
        crewSourcedFromUnitId: this._props.crewSourcedFromUnitId,
        completeFlightInstanceId: this._props.completeFlightInstanceId,
        isCrewAvailable: this._props.isCrewAvailable,
        updatePhoneNumbersFromExternal: this._props.updatePhoneNumbersFromExternal,
        crewSourcedToUnits: this._props.crewSourcedToUnits,
        excludeStates: this._props.excludeStates,
        parentUnitId: this._props.parentUnitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitEntities = (state: AppState) => state.entityStores.unit.entities;

export const selectUnitById = (id: string) => (state: AppState) =>
  state.entityStores.unit.entities.get(id);

export const selectUnitMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unit.meta.get(id);

export const selectUnitAndMetadataById = (id: string) =>
  createSelector(selectUnitById(id), selectUnitMetadataById(id), (unit, meta) => ({ unit, meta }));

export function selectUnitEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitEntitiesByName(name: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectUnitEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectUnitEntitiesByStatus(status: UnitStatus) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectUnitEntitiesByWeatherStatus(weatherStatus: WeatherStatus) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.weatherStatus === weatherStatus).valueSeq(),
  );
}

export function selectUnitEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectUnitEntitiesByIsAdministrative(isAdministrative: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isAdministrative === isAdministrative).valueSeq(),
  );
}

export function selectUnitEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectUnitEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectUnitEntitiesByIsHidden(isHidden: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isHidden === isHidden).valueSeq(),
  );
}

export function selectUnitEntitiesByIsThirdPartyUnit(isThirdPartyUnit: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isThirdPartyUnit === isThirdPartyUnit).valueSeq(),
  );
}

export function selectUnitEntitiesByEtloInMinutes(etloInMinutes: number) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.etloInMinutes === etloInMinutes).valueSeq(),
  );
}

export function selectUnitEntitiesByPilotNotes(pilotNotes: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.pilotNotes === pilotNotes).valueSeq(),
  );
}

export function selectUnitEntitiesByLastPilotUpdateTime(lastPilotUpdateTime: Date) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.lastPilotUpdateTime === lastPilotUpdateTime).valueSeq(),
  );
}

export function selectUnitEntitiesByLastPilotUpdateById(lastPilotUpdateById: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.lastPilotUpdateById === lastPilotUpdateById).valueSeq(),
  );
}

export function selectUnitEntitiesByIsExternallyManaged(isExternallyManaged: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isExternallyManaged === isExternallyManaged).valueSeq(),
  );
}

export function selectUnitEntitiesByMaximumFlightRule(maximumFlightRule: FlightRule) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.maximumFlightRule === maximumFlightRule).valueSeq(),
  );
}

export function selectUnitEntitiesByIsOnDelay(isOnDelay: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isOnDelay === isOnDelay).valueSeq(),
  );
}

export function selectUnitEntitiesByPlannedAvailableNotes(plannedAvailableNotes: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.plannedAvailableNotes === plannedAvailableNotes).valueSeq(),
  );
}

export function selectUnitEntitiesByPlannedAvailableTime(plannedAvailableTime: Date) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.plannedAvailableTime === plannedAvailableTime).valueSeq(),
  );
}

export function selectUnitEntitiesByIsEnabled(isEnabled: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isEnabled === isEnabled).valueSeq(),
  );
}

export function selectUnitEntitiesByIsSpare(isSpare: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isSpare === isSpare).valueSeq(),
  );
}

export function selectUnitEntitiesByOemPlacardNumber(oemPlacardNumber: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.oemPlacardNumber === oemPlacardNumber).valueSeq(),
  );
}

export function selectUnitEntitiesByPrimaryOutOfServiceEventId(primaryOutOfServiceEventId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.primaryOutOfServiceEventId === primaryOutOfServiceEventId).valueSeq(),
  );
}

export function selectUnitEntitiesByEmSystemResourceId(emSystemResourceId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.emSystemResourceId === emSystemResourceId).valueSeq(),
  );
}

export function selectUnitEntitiesByOnBaseETAInMinutes(onBaseETAInMinutes: number) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.onBaseETAInMinutes === onBaseETAInMinutes).valueSeq(),
  );
}

export function selectUnitEntitiesByCrewSourcedFromUnitId(crewSourcedFromUnitId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.crewSourcedFromUnitId === crewSourcedFromUnitId).valueSeq(),
  );
}

export function selectUnitEntitiesByCompleteFlightInstanceId(completeFlightInstanceId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.completeFlightInstanceId === completeFlightInstanceId).valueSeq(),
  );
}

export function selectUnitEntitiesByIsCrewAvailable(isCrewAvailable: boolean) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.isCrewAvailable === isCrewAvailable).valueSeq(),
  );
}

export function selectUnitEntitiesByUpdatePhoneNumbersFromExternal(
  updatePhoneNumbersFromExternal: boolean,
) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities
      .filter((e) => e.updatePhoneNumbersFromExternal === updatePhoneNumbersFromExternal)
      .valueSeq(),
  );
}

export function selectUnitEntitiesByParentUnitId(parentUnitId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.parentUnitId === parentUnitId).valueSeq(),
  );
}

export function selectUnitEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
